import { cookieDomain, getCookie, type RemoteConfig, setCookie } from "@repo/core-utils";
import { LogSentryMessage } from "@repo/core-utils";
import flagsmith from "flagsmith";
import type { IFlags } from "flagsmith/types";

const REMOTE_CONFIG_COOKIE_NAME = "rc";

export type { RemoteConfig } from "@repo/core-utils";

export type RemoteConfigTraits = Record<string, string | number | boolean>;

export type RemoteConfigClientConfig = {
  environmentID: string;
  identityID: string;
  traits?: RemoteConfigTraits;
};
export class RemoteConfigClient {
  private readonly DOMAIN = cookieDomain;
  private cachedConfig: Record<string, string> = {};
  private overrideConfig: Record<string, string> = {};
  private remoteOnlyConfig: RemoteConfig = {};

  public constructor({ environmentID, identityID, traits }: RemoteConfigClientConfig) {
    this.cachedConfig = this.getCachedConfig();
    flagsmith.identify(identityID, traits);
    flagsmith.init({
      environmentID,
      preventFetch: true,
      api: "https://edge.api.flagsmith.com/api/v1/",
      onError: (error) => {
        LogSentryMessage({
          message: "Flagsmith error",
          level: "info",
          extra: { error },
        });
      },
    });
  }

  private updateCachedConfig(newConfig: Record<string, string>) {
    const cachedConfig = this.getCachedConfig();
    const cachedKeys = Object.keys(cachedConfig).sort();
    const remoteKeys = Object.keys(newConfig).sort();

    const staleKeys = cachedKeys.filter((x) => !remoteKeys.includes(x));
    staleKeys.forEach((key) => delete cachedConfig[key]);

    const newKeys = remoteKeys.filter((x) => !cachedKeys.includes(x));
    newKeys.forEach((key) => (cachedConfig[key] = newConfig[key]));

    const crossDeviceKeys = remoteKeys.filter((key) => key.endsWith("-cd"));
    crossDeviceKeys.forEach((key) => (cachedConfig[key] = newConfig[key]));

    this.setCachedConfig(cachedConfig);
  }

  public async identify(identityID: string, traits?: RemoteConfigTraits) {
    await flagsmith.identify(identityID, traits);
    this.processConfig();
  }

  public setFlags(flags: IFlags<string>) {
    flagsmith.setState({ ...flagsmith.getState(), flags });
    this.processConfig();
  }

  private processConfig() {
    const configs = flagsmith.getState().flags;
    if (configs && Object.keys(configs).length > 0) {
      const remoteConfig = {} as Record<string, string>;
      Object.entries(configs).forEach(([name, { value, enabled }]) => {
        if (name.endsWith("-ff")) {
          this.remoteOnlyConfig[name] = enabled && value !== false;
        } else {
          if (enabled) {
            remoteConfig[name] = value?.toString() ?? "control";
          }
        }
      });

      this.updateCachedConfig(remoteConfig);
    }
  }

  private getCachedConfig() {
    const cookie = getCookie(REMOTE_CONFIG_COOKIE_NAME);
    return cookie ? JSON.parse(cookie) : {};
  }
  private setCachedConfig(config: Record<string, string>) {
    this.cachedConfig = config;
    setCookie(REMOTE_CONFIG_COOKIE_NAME, JSON.stringify(config), 24 * 7, this.DOMAIN);
  }

  public addOverride(configName: string, configValue: string) {
    this.overrideConfig[configName] = configValue;
  }

  public removeOverride(configName: string) {
    delete this.overrideConfig[configName];
  }

  public getConfig(configName: string) {
    return this.getAllConfig()[configName];
  }

  public getAllConfig(): RemoteConfig {
    return {
      ...this.remoteOnlyConfig,
      ...this.cachedConfig,
      ...this.overrideConfig,
    };
  }
}
